import { ReactNode, createContext, useContext, useState } from 'react';
import { findCatalogCardSizeVariant } from '../sizing/catalogCardSize.consts';
import { CatalogCardSizeVariant } from '../sizing/catalogCardSize.types';
import {
  CatalogViewCatalogFields,
  CatalogViewMode,
  CatalogViewUserFields,
  CatalogViewUserPreference,
} from '../view/catalogView.types';
import { resolveEffectiveView } from '../view/resolveEffectiveView';

interface CatalogViewSource<T> {
  data: T | null;
  isSettled: boolean;
}

interface CatalogViewContextValue {
  effectiveMode: CatalogViewMode;
  gridDataInclude: boolean;
  variant: CatalogCardSizeVariant;
  applied: CatalogViewUserFields;
  setApplied: (next: CatalogViewUserFields) => void;
}

const CatalogViewContext = createContext<CatalogViewContextValue | null>(null);

interface CatalogViewSyncedSources {
  preference: boolean;
  catalog: boolean;
}

interface CatalogViewAppliedState {
  applied: CatalogViewUserFields;
  hasUserApplied: boolean;
  syncedSources: CatalogViewSyncedSources;
}

interface ResolveSourceSyncParams {
  state: CatalogViewAppliedState;
  userPreference: CatalogViewSource<CatalogViewUserPreference>;
  catalogConfig: CatalogViewSource<CatalogViewCatalogFields>;
}

/**
 * `null` means "nothing to sync", and it is what makes the render adjustment converge:
 * a source is only ever read once, and the recomputed value is always the effective one
 * (the precedence result), never a single source's.
 */
const resolveSourceSync = ({
  state,
  userPreference,
  catalogConfig,
}: ResolveSourceSyncParams): CatalogViewAppliedState | null => {
  if (state.hasUserApplied) {
    return null;
  }

  const hasNewPreference = userPreference.isSettled && !state.syncedSources.preference;
  const hasNewCatalog = catalogConfig.isSettled && !state.syncedSources.catalog;

  if (!hasNewPreference && !hasNewCatalog) {
    return null;
  }

  return {
    applied: resolveEffectiveView({
      userPreference: userPreference.data,
      catalogConfig: catalogConfig.data,
    }),
    hasUserApplied: false,
    syncedSources: {
      preference: state.syncedSources.preference || hasNewPreference,
      catalog: state.syncedSources.catalog || hasNewCatalog,
    },
  };
};

interface CatalogViewProviderProps {
  children: ReactNode;
  userPreference: CatalogViewSource<CatalogViewUserPreference>;
  catalogConfig: CatalogViewSource<CatalogViewCatalogFields>;
  isOfflineMode?: boolean;
}

export const CatalogViewProvider = ({
  children,
  userPreference,
  catalogConfig,
  isOfflineMode = false,
}: CatalogViewProviderProps) => {
  const [state, setState] = useState<CatalogViewAppliedState>(() => ({
    applied: resolveEffectiveView({
      userPreference: userPreference.data,
      catalogConfig: catalogConfig.data,
    }),
    hasUserApplied: false,
    syncedSources: { preference: userPreference.isSettled, catalog: catalogConfig.isSettled },
  }));

  const syncedState = resolveSourceSync({ state, userPreference, catalogConfig });

  if (syncedState) {
    setState(syncedState);
  }

  const setApplied = (next: CatalogViewUserFields) => {
    setState((current) => ({ ...current, applied: next, hasUserApplied: true }));
  };

  const effectiveMode: CatalogViewMode = isOfflineMode ? CatalogViewMode.CARD : state.applied.mode;

  const value: CatalogViewContextValue = {
    effectiveMode,
    gridDataInclude: effectiveMode === CatalogViewMode.LIST,
    variant: findCatalogCardSizeVariant(state.applied.cardSize),
    applied: state.applied,
    setApplied,
  };

  return <CatalogViewContext.Provider value={value}>{children}</CatalogViewContext.Provider>;
};

export const useCatalogView = (): CatalogViewContextValue => {
  const context = useContext(CatalogViewContext);

  if (!context) {
    throw new Error('useCatalogView must be used within a CatalogViewProvider');
  }

  return context;
};
